import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient } from 'react-query';
import { useConfirm } from 'material-ui-confirm';
import { enqueueSnackbar } from 'notistack';
import parse from 'html-react-parser';
import { translate as t } from 'hooks/useTranslations';
import { AppContext } from 'views/App/context';
import { DraftManualDetailContext } from '../context';
import useDraftPolicy from 'views/App/Portal/Policy/DraftManuals/_data_sources/useDraftPolicy';
import useManagePolicy from 'views/App/Portal/Policy/DraftManuals/_data_sources/useManagePolicy';
import draftManualHelper from '../../helpers/draftManualHelper';
import { formatDateForLoad, formatDateForSave } from 'views/App/Portal/Policy/_helpers/policyHeaderDateHelper';
import { checkForBlankHeaders } from 'views/App/Portal/Policy/_helpers/policyHeaderHelper';
import {
  DraftManual,
  DraftPolicy,
  DraftPolicySection,
  DraftPolicySubSection,
  AttachmentManual,
  CrossReference,
} from '../../../_types/manuals/draft';
import { ManualFilterOptions } from '../../../_types/enums';
import { ServerErrorResponse } from 'data_sources/constants';
import errorHelper from 'helpers/errorHelper';
import { Box } from '@mui/material';
import _, { debounce } from 'lodash';
import { useTrackChanges } from 'components/ContentEditor/context/SharedTrackChangesContext';
import useManageManual from 'views/App/Portal/Policy/DraftManuals/_data_sources/useManageManual';
import useAccreditationTags from 'views/App/Portal/AWB/_data_sources/useAccreditationTags';
import useManageIssuedChangeDecision from 'views/App/Portal/Policy/DraftManuals/Detail/ReviewUpdates/_data_sources/useManageIssuedChangeDecision';

/***********
 * VIEW - Not locked, no edit permission
 * DISABLED - policy is currently disabled
 * EDITABLE - Not locked, has edit permission
 * EDITING - locked by current user
 * LOCKED - locked by another user
 ******/
type PolicyMode = 'VIEW' | 'DISABLED' | 'EDITABLE' | 'EDITING' | 'LOCKED';

interface PolicyEditorContextProps {
  autoSave: () => Promise<void>;
  committedPolicyQuery: any;
  currentSection: string | null;
  disableSave: (policyData: DraftPolicy | null) => boolean;
  enabledFilter: boolean;
  disabledFilter: boolean;
  filteredSections: () => DraftPolicySection[] | null | undefined;
  filterSubsections: (subSections: DraftPolicySubSection[]) => DraftPolicySubSection[];
  gettingLock: boolean;
  id: string;
  isOpenModifyPolicy: boolean;
  isSaving: boolean;
  handleAcquireLockClick: () => Promise<void>;
  handleInsertSectionAfter: (sectionId: string) => void;
  handleInsertSubSectionAfter: (sectionId: string, subsectionId?: string) => void;
  handleModifyClick: () => Promise<void>;
  handleModifyPolicyDone: () => void;
  handleCancel: () => Promise<boolean>;
  handleSave: () => Promise<void>;
  handleSectionChange: (updatedItem: DraftPolicySubSection | DraftPolicySection) => void;
  handleSelectSection: (sectionId: string) => void;
  handleUpdateDraftStatus: (newStatus: string, callback?: () => void) => Promise<void>;
  policyData: DraftPolicy | null;
  policyMode: PolicyMode;
  policyNumber: string;
  policyQuery: any;
  revertChanges: () => Promise<void>;
  setEnabledFilter: (enabled: boolean) => void;
  setDisabledFilter: (disabled: boolean) => void;
  setGettingLock: React.Dispatch<React.SetStateAction<boolean>>;
  setIsOpenModifyPolicy: React.Dispatch<React.SetStateAction<boolean>>;
  updatePolicyData: (updatedData: any) => void;
  addAttachmentsToSection: (sectionId: string | null, newAttachments: AttachmentManual[]) => void;
  acceptNewPolicy: () => Promise<void>;
  rejectNewPolicy: () => Promise<void>;
  acceptDisabledPolicy: () => Promise<void>;
  rejectDisabledPolicy: () => Promise<void>;
  isAcceptingNew: boolean;
  isRejectingNew: boolean;
  isAcceptingDisabled: boolean;
  isRejectingDisabled: boolean;
  addCrossReferenceToSection: (sectionId: string | null, crossReference: CrossReference) => void;
  removeCrossReferenceFromSection: (sectionId: string | null, crossReferenceId: string) => void;
  accreditationTags: Record<string, string[]>;
}

export const PolicyEditorContext = createContext<PolicyEditorContextProps | undefined>(undefined);

export const PolicyEditorProvider: React.FC<{ id: string; children: React.ReactNode; }> = ({ id, children }) => {
  const queryClient = useQueryClient();
  const confirm = useConfirm();
  const { currentProfile } = useContext(AppContext);
  const {
    manual,
    manualHasPermissions,
    pendingUpdates,
    enabledFilter,
    setEnabledFilter,
    disabledFilter,
    setDisabledFilter,
  } = useContext(DraftManualDetailContext);
  const { setTrackChangesState } = useTrackChanges();
  const policyQuery = useDraftPolicy(manual.id, id);
  const [policyData, setPolicyData] = useState<DraftPolicy | null>(null);
  const [currentSection, setCurrentSection] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [gettingLock, setGettingLock] = useState<boolean>(false);
  const [hasLock, setHasLock] = useState<string | null>(null);
  const [isOpenModifyPolicy, setIsOpenModifyPolicy] = useState<boolean>(false);
  const [policyMode, setPolicyMode] = useState<PolicyMode>('VIEW');
  const [isAcceptingNew, setIsAcceptingNew] = useState(false);
  const [isRejectingNew, setIsRejectingNew] = useState(false);
  const [isAcceptingDisabled, setIsAcceptingDisabled] = useState(false);
  const [isRejectingDisabled, setIsRejectingDisabled] = useState(false);
  const { approveChanges, getLock, removeLock, takeLock, update: updatePolicy, updateDraftStatus } = useManagePolicy();
  const { update: updateManual, getLock: getManualLock, removeLock: removeManualLock } = useManageManual();
  const { processIssuedPolicyChangeDecision } = useManageIssuedChangeDecision();
  const policyNumber = draftManualHelper.getNumberManualNode(policyQuery.data, manual);
  const committedPolicyQuery = useDraftPolicy(manual.id, id, false);
  const [transformedAccreditationTags, setTransformedAccreditationTags] = useState<Record<string, string[]>>({});
  const accreditationTagsQuery = useAccreditationTags(manual.id, id);

  useEffect(() => {
    if (policyQuery.data) {
      setPolicyData(policyQuery.data);
    }
  }, [policyQuery.data]);

  useEffect(() => {
    let mode: PolicyMode = 'VIEW';
    if (manualHasPermissions(manual.id, ['updateDraftPolicy']) && policyQuery.data) {
      if (policyQuery.data.lock) {
        if (policyQuery.data.lock.userId === currentProfile?.user?.id) {
          mode = 'EDITING';
          setHasLock(policyQuery.data.id);
        } else {
          mode = 'LOCKED';
          if (hasLock === policyQuery.data.id) {
            enqueueSnackbar(
              <Box>
                {parse(
                  t('draftManuals.policy.lostLock', '', {
                    user: draftManualHelper.formatPolicyLockInfo(policyQuery.data),
                  })
                )}
              </Box>,
              {
                variant: 'error',
                autoHideDuration: 10000,
              }
            );
            setHasLock(null);
          }
        }
      } else {
        mode = 'EDITABLE';
      }
      if (policyQuery.data.state === 'DISABLED') {
        mode = 'DISABLED';
      }
      if (policyMode === 'EDITING' && mode === 'DISABLED') {
        enqueueSnackbar(
          <div
            dangerouslySetInnerHTML={{
              __html: t('draftManuals.policy.savedButDisabled'),
            }}
          />,
          {
            variant: 'error',
            autoHideDuration: 10000,
          }
        );
      }
    }

    setPolicyMode(mode);
  }, [manualHasPermissions, manual.id, policyQuery.data, currentProfile?.user?.id]);

  useEffect(() => {
    if (accreditationTagsQuery.data) {
      const transformed = accreditationTagsQuery.data.reduce(
        (acc, item) => {
          const contentId = item.contentUri.split('/').pop() || '';
          const standardText = `${item.bodyAbbreviation} ${item.bodyVersionAbbreviation} ${item.standardHeading}: ${item.standardTitle}`;

          if (!acc[contentId]) {
            acc[contentId] = [];
          }
          acc[contentId].push(standardText);
          return acc;
        },
        {} as Record<string, string[]>
      );

      setTransformedAccreditationTags(transformed);
    }
  }, [accreditationTagsQuery.data]);

  const updatePolicyData = useCallback((updatedData: any) => {
    setPolicyData((prev) => ({ ...prev, ...updatedData }));
  }, []);

  const prepareAndUpdatePolicy = useCallback(
    (
      policyData: DraftPolicy | null,
      onSuccess?: (data: any) => void,
      onError?: (errors: ServerErrorResponse) => void
    ) => {
      if (!policyData) return;

      // If the policy has no saved headers yet, initialize them from the manual's
      // header configuration defaults so they aren't stored as an empty array.
      let resolvedHeaders = policyData.headers;
      if ((!resolvedHeaders || resolvedHeaders.length === 0) && manual.headerConfiguration?.order) {
        const manualHeaderConfig = manual.headerConfiguration;
        const defaultHeaders: any[] = [];
        manualHeaderConfig.order?.forEach((orderItem: any) => {
          if (orderItem.state !== 'ENABLED') return;
          const base = { type: orderItem.type, state: orderItem.state };
          switch (orderItem.type) {
            case 'EFFECTIVE_DATE':
              defaultHeaders.push({ ...base, dateFormat: manualHeaderConfig.effectiveDateFormat, autoSetDate: manualHeaderConfig.autoSetEffectiveDate, value: null });
              break;
            case 'REVISED_DATE':
              defaultHeaders.push({ ...base, dateFormat: manualHeaderConfig.revisedDateFormat, autoSetDate: manualHeaderConfig.autoSetRevisedDate, value: null });
              break;
            case 'AGENCY_LOGO':
              defaultHeaders.push({ ...base, agencyLogo: manualHeaderConfig.agencyLogo || null });
              break;
            case 'ISSUING_AUTHORITY':
              defaultHeaders.push({ ...base, issuingAuthorityType: manualHeaderConfig.issuingAuthorityType, issuingAuthoritySignature: manualHeaderConfig.issuingAuthoritySignature || null, issuingAuthorityName: manualHeaderConfig.issuingAuthorityName || null });
              break;
            case 'CUSTOM1':
            case 'CUSTOM2': {
              const customIndex = orderItem.type === 'CUSTOM1' ? 0 : 1;
              const customField = manualHeaderConfig.customFields?.[customIndex];
              defaultHeaders.push({ ...base, header: customField?.header || null, value: customField?.value || null });
              break;
            }
          }
        });
        resolvedHeaders = defaultHeaders;
      }

      const updatedPolicy: DraftPolicy = {
        ...policyData,
        headers: resolvedHeaders?.map((header: any) => {
          switch (header.type) {
            case 'ISSUING_AUTHORITY':
              return { ...header, issuingAuthorityType: manual.headerConfiguration?.issuingAuthorityType };
            case 'EFFECTIVE_DATE': {
              const effectiveDateFormat = manual.headerConfiguration?.effectiveDateFormat;
              const reformattedEffective = header.value
                ? (formatDateForSave(formatDateForLoad(header.value), effectiveDateFormat) ?? header.value)
                : header.value;
              return { ...header, dateFormat: effectiveDateFormat, autoSetDate: manual.headerConfiguration?.autoSetEffectiveDate, value: reformattedEffective };
            }
            case 'REVISED_DATE': {
              const revisedDateFormat = manual.headerConfiguration?.revisedDateFormat;
              const reformattedRevised = header.value
                ? (formatDateForSave(formatDateForLoad(header.value), revisedDateFormat) ?? header.value)
                : header.value;
              return { ...header, dateFormat: revisedDateFormat, autoSetDate: manual.headerConfiguration?.autoSetRevisedDate, value: reformattedRevised };
            }
            default:
              return header;
          }
        }),
        sections: policyData.sections.map((section) => {
          // Process section attachments
          let sectionContent = section.content || '';
          // Remove existing attachment markup
          sectionContent = sectionContent.replace(
            /<p><a href="" data-attachment-id="[^"]*" data-manual-id="[^"]*" title="[^"]*">[^<]*<\/a><\/p>/g,
            ''
          );

          if (section.attachments) {
            section.attachments.forEach((attachment) => {
              if (!attachment.embedded) {
                const manualAttachment = manual.attachments.find((a) => a.id === attachment.id);
                if (manualAttachment) {
                  sectionContent += `<p><a href="" data-attachment-id="${attachment.id}" data-manual-id="${manual.id}" title="${manualAttachment.name}">${manualAttachment.name}</a></p>`;
                }
              }
            });
          }

          return {
            ...section,
            content: sectionContent,
            crossReferences: section.crossReferences || [],
            isDeleted: section.isDeleted ?? false,
            subSections: section.subSections.map((subsection) => {
              // Process subsection attachments
              let subsectionContent = subsection.content || '';
              // Remove existing attachment markup
              subsectionContent = subsectionContent.replace(
                /<p><a href="" data-attachment-id="[^"]*" data-manual-id="[^"]*" title="[^"]*">[^<]*<\/a><\/p>/g,
                ''
              );

              if (subsection.attachments) {
                subsection.attachments.forEach((attachment) => {
                  if (!attachment.embedded) {
                    const manualAttachment = manual.attachments.find((a) => a.id === attachment.id);
                    if (manualAttachment) {
                      subsectionContent += `<p><a href="" data-attachment-id="${attachment.id}" data-manual-id="${manual.id}" title="${manualAttachment.name}">${manualAttachment.name}</a></p>`;
                    }
                  }
                });
              }

              return {
                ...subsection,
                content: subsectionContent,
                crossReferences: subsection.crossReferences || [],
                isDeleted: subsection.isDeleted ?? false,
              };
            }),
          };
        }),
      };

      updatedPolicy.sections.forEach((section) => {
        if (section.id.startsWith('new_')) {
          section.id = '';
        }
        if (_.isEmpty(section.title)) section.title = 'Section Title';
        if (_.isEmpty(section.editLevelTag)) section.editLevelTag = 'BEST_PRACTICE';
        section.subSections.forEach((subsection) => {
          if (subsection.id.startsWith('new_')) {
            subsection.id = '';
          }
          if (_.isEmpty(subsection.title)) subsection.title = 'Section Title';
          if (_.isEmpty(subsection.editLevelTag)) subsection.editLevelTag = 'BEST_PRACTICE';
        });
      });

      updatePolicy(
        manual.id,
        id,
        updatedPolicy,
        (data) => {
          if (data) {
            setPolicyData((prev) => {
              if (!prev) return data;
              prev.sections.forEach((section, sectionIndex) => {
                if (section.id.startsWith('new_') || section.id === '') {
                  data.sections.forEach((newSection, nsi) => {
                    if (nsi === sectionIndex) {
                      section.id = newSection.id;
                    }
                  });
                }
                section.subSections.forEach((subsection, subSectionIndex) => {
                  if (subsection.id.startsWith('new_') || subsection.id === '') {
                    data.sections.forEach((newSection, nsi) => {
                      if (nsi === sectionIndex) {
                        newSection.subSections.forEach((newSubSection, nssi) => {
                          if (nssi === subSectionIndex) {
                            subsection.id = newSubSection.id;
                          }
                        });
                      }
                    });
                  }
                });
              });
              return prev;
            });
          }
          if (onSuccess) onSuccess(data);
        },
        (errors: ServerErrorResponse) => {
          if (errors.errorInformationCode === 3134) {
            queryClient.invalidateQueries(['draft_manuals']);
            queryClient.invalidateQueries(['draft_manual', manual.id]);
            queryClient.invalidateQueries(['draft_policy', manual.id, id]);
          } else {
            errorHelper.enqueueErrors(errors);
          }
          if (onError) onError(errors);
        }
      );
    },
    [manual.id, id, updatePolicy, queryClient]
  );

  const handleSave = useCallback(async () => {
    setIsSaving(true);
    setTrackChangesState(false);
    prepareAndUpdatePolicy(
      policyData,
      () => {
        removeLock(
          manual.id,
          id,
          () => {
            setPolicyData(null);
            queryClient.invalidateQueries(['draft_manuals']);
            queryClient.invalidateQueries(['draft_manual', manual.id]);
            queryClient.invalidateQueries(['draft_policy', manual.id, id]);
            setIsSaving(false);
          },
          (error: ServerErrorResponse) => {
            errorHelper.enqueueErrors(error);
            setIsSaving(false);
          }
        );
      },
      (errors: ServerErrorResponse) => {
        errorHelper.enqueueErrors(errors);
        setIsSaving(false);
      }
    );
  }, [manual, policyData, queryClient, prepareAndUpdatePolicy, removeLock, setTrackChangesState]);

  const autoSave = useCallback(async () => {
    if (policyData) {
      // console.log('autoSave', policyData);
      prepareAndUpdatePolicy(policyData, (data) => {
        // After successful save, filter out deleted items from local state
        if (data) {
          setPolicyData((prev) => {
            if (!prev) return data;

            // Filter out deleted sections and subsections
            const filteredSections = prev.sections
              .filter((section) => !section.isDeleted)
              .map((section) => ({
                ...section,
                subSections: section.subSections.filter((subsection) => !subsection.isDeleted),
              }));

            return { ...prev, sections: filteredSections };
          });
        }
      });
    }
  }, [policyData, prepareAndUpdatePolicy]);

  const revertChanges = useCallback(
    async (options?: { updateLocalState?: boolean }) => {
      if (!committedPolicyQuery.data?.sections) return;
      const updateLocalState = options?.updateLocalState !== false;
      const updatedPolicy: DraftPolicy = {
        ...committedPolicyQuery.data,
      };
      if (updateLocalState) {
        setPolicyData(null);
      }
      await updatePolicy(
        manual.id,
        id,
        updatedPolicy,
        (data) => {
          if (updateLocalState && data) {
            setPolicyData(data);
          }
          queryClient.invalidateQueries(['draft_manuals']);
          queryClient.invalidateQueries(['draft_manual', manual.id]);
          queryClient.invalidateQueries(['draft_policy', manual.id, id]);
        },
        (errors: ServerErrorResponse) => {
          if (errors.errorInformationCode === 3134) {
            queryClient.invalidateQueries(['draft_manuals']);
            queryClient.invalidateQueries(['draft_manual', manual.id]);
            queryClient.invalidateQueries(['draft_policy', manual.id, id]);
          } else {
            errorHelper.enqueueErrors(errors);
          }
        }
      );
    },
    [committedPolicyQuery.data, manual.id, id, queryClient, updatePolicy]
  );

  const releaseLockAndCleanup = useCallback(() => {
    setTrackChangesState(false);
    setPolicyData(null);
    queryClient.invalidateQueries(['draft_manuals']);
    queryClient.invalidateQueries(['draft_manual', manual.id]);
    queryClient.invalidateQueries(['draft_policy', manual.id, id]);
  }, [manual.id, id, queryClient, setTrackChangesState]);

  const handleCancel = useCallback(async (): Promise<boolean> => {
    if (policyMode !== 'EDITING' || !policyData || !committedPolicyQuery.data) {
      return false;
    }
    const hasStagedChanges =
      JSON.stringify(committedPolicyQuery.data) !== JSON.stringify(policyData);
    if (!hasStagedChanges) {
      localStorage.removeItem(`policy-lock_${id}`);
      removeLock(manual.id, id, releaseLockAndCleanup, (error: ServerErrorResponse) => {
        errorHelper.enqueueErrors(error);
      });
      return true;
    }
    try {
      await confirm({
        cancellationButtonProps: {
          variant: 'outlined',
          color: 'neutral',
        },
        cancellationText: t('draftManuals.policy.discardChanges.cancel'),
        confirmationButtonProps: {
          variant: 'contained',
          color: 'error',
        },
        confirmationText: t('draftManuals.policy.discardChanges.confirm'),
        description: t('draftManuals.policy.discardChanges.body'),
        title: t('draftManuals.policy.discardChanges.title'),
      });
    } catch {
      return false;
    }
    localStorage.removeItem(`policy-lock_${id}`);
    await revertChanges({ updateLocalState: false });
    removeLock(
      manual.id,
      id,
      () => {
        setTrackChangesState(false);
        queryClient.invalidateQueries(['draft_manuals']);
        queryClient.invalidateQueries(['draft_manual', manual.id]);
        queryClient.invalidateQueries(['draft_policy', manual.id, id]);
      },
      (error: ServerErrorResponse) => {
        errorHelper.enqueueErrors(error);
      }
    );
    return true;
  }, [
    policyMode,
    policyData,
    committedPolicyQuery.data,
    id,
    manual.id,
    confirm,
    releaseLockAndCleanup,
    removeLock,
    revertChanges,
  ]);

  const handleSelectSection = useCallback(
    (sectionId: string) => {
      if (currentSection !== sectionId && policyData) {
        setCurrentSection(sectionId);
        if (JSON.stringify(policyData) !== JSON.stringify(policyQuery.data) && !sectionId?.startsWith('new_')) {
          autoSave();
        }
      }
    },
    [policyData, policyQuery.data, currentSection]
  );

  const handleModifyClick = useCallback(async () => {
    setGettingLock(true);
    await getLock(
      manual.id,
      id,
      (lock) => {
        queryClient.invalidateQueries(['draft_manuals']);
        queryClient.invalidateQueries(['draft_manual', manual.id]);
        queryClient.invalidateQueries(['draft_policy', manual.id, id]);
        setGettingLock(false);
      },
      (error: ServerErrorResponse) => {
        errorHelper.enqueueErrors(error);
        setGettingLock(false);
      }
    );
  }, [manual, setGettingLock, queryClient]);

  const handleAcquireLockClick = useCallback(async () => {
    setGettingLock(true);
    await confirm({
      cancellationButtonProps: {
        variant: 'outlined',
        color: 'neutral',
      },
      confirmationButtonProps: {
        variant: 'contained',
        color: 'error',
      },
      confirmationText: t('draftManuals.policy.acquireLock.ok'),
      description: t('draftManuals.policy.acquireLock.body'),
      title: t('draftManuals.policy.acquireLock.title'),
    }).then(
      async () =>
        await new Promise<void>((resolve, reject) => {
          takeLock(
            manual.id,
            id,
            () => {
              setPolicyData(null);
              localStorage.setItem(`policy-lock_${id}`, 'true');
              queryClient.invalidateQueries(['draft_manuals']);
              queryClient.invalidateQueries(['draft_manual', manual.id]);
              queryClient.invalidateQueries(['draft_policy', manual.id, id]);
              setEnabledFilter(true);
              setGettingLock(false);
              enqueueSnackbar(<Box>{parse(t('draftManuals.policy.acquireLock.success'))}</Box>, {
                variant: 'success',
                autoHideDuration: 5000,
              });
              resolve();
            },
            (error: ServerErrorResponse) => {
              errorHelper.enqueueErrors(error);
              setGettingLock(false);
              reject(error);
            }
          );
        }).catch(() => {
          // don't do it
          setGettingLock(false);
        })
    );
  }, []);

  const handleUpdateDraftStatus = useCallback(
    async (newStatus: string, callback?: () => void) => {
      if (newStatus === 'APPROVED') {
        const doApprove = async () => {
          await approveChanges(
            manual.id,
            id,
            () => {
              queryClient.invalidateQueries(['draft_manuals']);
              queryClient.invalidateQueries(['approved_content']);
              queryClient.invalidateQueries(['draft_manual', manual.id]);
              queryClient.invalidateQueries(['draft_policy', manual.id, id]);
              if (callback) callback();
            },
            (error: ServerErrorResponse) => {
              errorHelper.enqueueErrors(error);
            }
          );
        };

        if (checkForBlankHeaders(policyData?.headers, manual.headerConfiguration, manual.manualLevel)) {
          try {
            await confirm({
              title: t('draftManuals.manualProperties.policyHeaders.blankHeadersWarning.title'),
              description: t('draftManuals.manualProperties.policyHeaders.blankHeadersWarning.description'),
              confirmationText: t('common.yes'),
              cancellationText: t('draftManuals.manualProperties.policyHeaders.blankHeadersWarning.cancel'),
              confirmationButtonProps: { variant: 'contained', color: 'primary' },
              cancellationButtonProps: { variant: 'outlined', color: 'neutral' },
            });
            await doApprove();
          } catch {
            callback?.();
          }
        } else {
          await doApprove();
        }
      } else {
        updateDraftStatus(
          manual.id,
          id,
          newStatus,
          () => {
            queryClient.invalidateQueries(['draft_manuals']);
            queryClient.invalidateQueries(['draft_manual', manual.id]);
            queryClient.invalidateQueries(['draft_policy', manual.id, id]);
            if (callback) callback();
          },
          (error: ServerErrorResponse) => {
            errorHelper.enqueueErrors(error);
          }
        );
      }
    },
    [manual, id, updateDraftStatus, approveChanges, queryClient, confirm, policyData]
  );

  const handleInsertSectionAfter = useCallback(
    (sectionId: string) => {
      if (policyData) {
        const newId = `new_${Date.now()}`;
        updatePolicyData(draftManualHelper.insertSectionAfter(sectionId, newId, policyData));

        handleSelectSection(newId);
      }
    },
    [policyData]
  );

  const handleInsertSubSectionAfter = useCallback(
    (sectionId: string, subsectionId?: string) => {
      if (policyData) {
        const newId = `new_${Date.now()}`;
        updatePolicyData(draftManualHelper.insertSubSectionAfter(sectionId, newId, policyData, subsectionId));

        handleSelectSection(newId);
      }
    },
    [policyData]
  );

  const filteredSections = useCallback(() => {
    if (enabledFilter && disabledFilter) return policyData?.sections;

    if (enabledFilter) return policyData?.sections.filter((section) => section.state === ManualFilterOptions.ENABLED);
    if (disabledFilter) {
      return policyData?.sections.filter(
        (section) =>
          section.state === ManualFilterOptions.DISABLED ||
          section.subSections.some((sub) => sub.state === ManualFilterOptions.DISABLED)
      );
    }
    return null;
  }, [enabledFilter, disabledFilter, policyData]);

  const filterSubsections = useCallback(
    (subSections: DraftPolicySubSection[]) => {
      if (enabledFilter && disabledFilter) return subSections;
      return subSections.filter(
        (sub) => sub.state === (enabledFilter ? ManualFilterOptions.ENABLED : ManualFilterOptions.DISABLED)
      );
    },
    [enabledFilter, disabledFilter]
  );

  const disableSave = useCallback(
    (policyData: DraftPolicy | null) => {
      if (!policyData) return true; // No policy data, disable save
      let disabled = isSaving;
      if (policyData.title.length === 0) disabled = true; // Title is empty, disable save
      policyData.sections.forEach((section) => {
        if (section.title.length === 0) disabled = true; // Section title is empty, disable save
        section.subSections.forEach((subsection) => {
          if (subsection.title.length === 0) disabled = true; // Subsection title is empty, disable save
        });
      });
      return disabled;
    },
    [isSaving]
  );

  const debouncedHandleSectionChange = useCallback(
    (updatedItem: DraftPolicySubSection | DraftPolicySection) => {
      setPolicyData((prevData) => {
        if (!prevData) return null;
        const updatedSections = prevData.sections.map((section) => {
          if (section.id === updatedItem.id) {
            // Handle section update
            const newSubSections =
              updatedItem.state === 'DISABLED'
                ? section.subSections.map((subSection) => ({ ...subSection, state: 'DISABLED' as 'DISABLED' }))
                : section.subSections;

            // If section is deleted, also delete all subsections
            const finalSubSections =
              (updatedItem as any).isDeleted === true
                ? newSubSections.map((subSection) => ({ ...subSection, isDeleted: true }))
                : newSubSections;

            return { ...section, ...updatedItem, subSections: finalSubSections };
          }

          // Handle subsection update
          const updatedSubSections = section.subSections.map((subSection) => {
            if (subSection.id === updatedItem.id) {
              return { ...subSection, ...updatedItem };
            }
            return subSection;
          });

          const isAnySubSectionEnabled = updatedSubSections.some((subSection) => subSection.state !== 'DISABLED');
          const shouldEnableSection = isAnySubSectionEnabled && section.state === 'DISABLED';

          return updatedSubSections !== section.subSections
            ? { ...section, subSections: updatedSubSections, state: shouldEnableSection ? 'ENABLED' : section.state }
            : section;
        });
        return { ...prevData, sections: updatedSections };
      });

      // Trigger auto-save after updating the policy data
      setTimeout(() => {
        document.body.dispatchEvent(new Event('auto-save-policy'));
      }, 500);
    },
    [policyData, setPolicyData]
  );
  const handleSectionChange = useMemo(
    () =>
      debounce((updatedItem: DraftPolicySubSection | DraftPolicySection) => {
        debouncedHandleSectionChange(updatedItem);
      }, 300),
    [debouncedHandleSectionChange]
  );

  useEffect(() => {
    return () => {
      handleSectionChange.cancel();
    };
  }, [handleSectionChange]);

  useEffect(() => {
    const autoSaveListener = () => {
      autoSave();
    };
    document.body.addEventListener('auto-save-policy', autoSaveListener);
    return () => {
      document.body.removeEventListener('auto-save-policy', autoSaveListener);
    };
  }, [autoSave, policyData]);

  /** Process marked deletes and close modify structure. Call when user clicks Done/Close in Modify Policy Structure. */
  const handleModifyPolicyDone = useCallback(() => {
    setPolicyData((prevData) => {
      if (!prevData?.sections) return prevData;
      const sectionsWithoutDeleted = prevData.sections
        .filter((section) => !section.isDeleted)
        .map((section) => ({
          ...section,
          subSections: (section.subSections ?? []).filter((sub) => !sub.isDeleted),
        }));
      return { ...prevData, sections: sectionsWithoutDeleted };
    });
    setIsOpenModifyPolicy(false);
    setTimeout(() => {
      document.body.dispatchEvent(new Event('auto-save-policy'));
    }, 0);
  }, [setIsOpenModifyPolicy]);

  const updateSubSections = (subSections: any[], targetId: string, newAttachments: any[]) => {
    return subSections.map((sub) => {
      if (sub.id === targetId) {
        return { ...sub, attachments: [...(sub.attachments || []), ...newAttachments] };
      }
      return sub;
    });
  };

  const updateSubSectionsCrossReferences = (
    subSections: DraftPolicySubSection[],
    targetId: string,
    crossReference: CrossReference,
    isAdding: boolean
  ) => {
    return subSections.map((sub) => {
      if (sub.id === targetId) {
        const currentCrossReferences = sub.crossReferences || [];
        const updatedCrossReferences = isAdding
          ? [...currentCrossReferences, crossReference]
          : currentCrossReferences.filter((ref: CrossReference) => ref.id !== crossReference.id);
        return { ...sub, crossReferences: updatedCrossReferences };
      }
      return sub;
    });
  };

  const addCrossReferenceToSection = (sectionId: string | null, crossReference: CrossReference) => {
    if (!sectionId) {
      return;
    }
    setPolicyData((prevPolicy) => {
      if (!prevPolicy) return prevPolicy;
      const updatedSections = prevPolicy.sections.map((section) => {
        if (section.id === sectionId) {
          const currentCrossReferences = Array.isArray(section.crossReferences) ? section.crossReferences : [];
          return { ...section, crossReferences: [...currentCrossReferences, crossReference] };
        }
        if (section.subSections) {
          return {
            ...section,
            subSections: updateSubSectionsCrossReferences(section.subSections, sectionId, crossReference, true),
          };
        }
        return section;
      });
      return { ...prevPolicy, sections: updatedSections };
    });

    // Trigger auto-save after updating the policy data
    setTimeout(() => {
      document.body.dispatchEvent(new Event('auto-save-policy'));
    }, 0);
  };

  const removeCrossReferenceFromSection = (sectionId: string | null, crossReferenceId: string) => {
    if (!sectionId) {
      return;
    }
    setPolicyData((prevPolicy) => {
      if (!prevPolicy) return prevPolicy;
      const updatedSections = prevPolicy.sections.map((section) => {
        if (section.id === sectionId) {
          const currentCrossReferences = section.crossReferences || [];
          return {
            ...section,
            crossReferences: currentCrossReferences.filter((ref: CrossReference) => ref.id !== crossReferenceId),
          };
        }
        if (section.subSections) {
          return {
            ...section,
            subSections: updateSubSectionsCrossReferences(
              section.subSections,
              sectionId,
              { id: crossReferenceId } as CrossReference,
              false
            ),
          };
        }
        return section;
      });
      return { ...prevPolicy, sections: updatedSections };
    });
  };

  const addAttachmentsToSection = (sectionId: string | null, newAttachments: any[]) => {
    if (!sectionId) {
      return;
    }
    setPolicyData((prevPolicy) => {
      if (!prevPolicy) return prevPolicy;
      const updatedSections = prevPolicy.sections.map((section) => {
        if (section.id === sectionId) {
          return { ...section, attachments: [...(section.attachments || []), ...newAttachments] };
        }
        if (section.subSections) {
          return { ...section, subSections: updateSubSections(section.subSections, sectionId, newAttachments) };
        }
        return section;
      });
      return { ...prevPolicy, sections: updatedSections };
    });
  };

  const acceptNewPolicy = useCallback(async () => {
    if (!policyData) return;

    setIsAcceptingNew(true);
    try {
      await new Promise<void>((resolve, reject) => {
        getManualLock(
          manual.id,
          (lock) => {
            // Create a deep copy of the manual
            const updatedManual: DraftManual = JSON.parse(JSON.stringify(manual));

            // Find the chapter containing our policy and update it
            updatedManual.chapters = updatedManual.chapters.map((chapter) => {
              // Skip if chapter has no policies
              if (!chapter.policies) return chapter;

              // Check if this chapter contains our policy
              const policyIndex = chapter.policies.findIndex((policy) => policy.id === id);
              if (policyIndex === -1) return chapter;

              // Update the policy - using type assertion since we know the policy has these properties
              const updatedPolicies = [...chapter.policies];

              // Create updated policy with the correct type
              const policy = updatedPolicies[policyIndex];
              const updatedPolicy = {
                ...policy,
                state: 'ENABLED' as 'ENABLED',
              };

              // Add isNew property using type assertion
              (updatedPolicy as any).isNew = false;

              updatedPolicies[policyIndex] = updatedPolicy;

              return {
                ...chapter,
                policies: updatedPolicies,
                // Set chapter state to ENABLED if it's not already
                state: chapter.state !== 'ENABLED' ? 'ENABLED' : chapter.state,
              };
            });

            updateManual(
              manual.id,
              updatedManual,
              () => {
                removeManualLock(
                  manual.id,
                  () => {
                    queryClient.invalidateQueries('draft_manuals');
                    queryClient.invalidateQueries(['draft_manual', manual.id]);
                    queryClient.invalidateQueries(['draft_policy', manual.id, id]);
                    setIsAcceptingNew(false);
                    enqueueSnackbar(t('draftManuals.policy.newPolicy.accepted'), { variant: 'success' });
                    resolve();
                  },
                  (error: ServerErrorResponse) => {
                    errorHelper.enqueueErrors(error);
                    setIsAcceptingNew(false);
                    reject(error);
                  }
                );
              },
              (error: ServerErrorResponse) => {
                removeManualLock(
                  manual.id,
                  () => { },
                  () => { }
                );
                errorHelper.enqueueErrors(error);
                setIsAcceptingNew(false);
                reject(error);
              }
            );
          },
          (error: ServerErrorResponse) => {
            errorHelper.enqueueErrors(error);
            setIsAcceptingNew(false);
            reject(error);
          }
        );
      });
    } catch (error) {
      console.error('Error accepting new policy:', error);
    }
  }, [policyData, manual, id, getManualLock, updateManual, removeManualLock, queryClient, t]);

  const rejectNewPolicy = useCallback(async () => {
    if (!policyData) return;

    setIsRejectingNew(true);
    try {
      await new Promise<void>((resolve, reject) => {
        getManualLock(
          manual.id,
          (lock) => {
            // Create a deep copy of the manual
            const updatedManual: DraftManual = JSON.parse(JSON.stringify(manual));

            // Find the chapter containing our policy and update it
            updatedManual.chapters = updatedManual.chapters.map((chapter) => {
              // Skip if chapter has no policies
              if (!chapter.policies) return chapter;

              // Check if this chapter contains our policy
              const policyIndex = chapter.policies.findIndex((policy) => policy.id === id);
              if (policyIndex === -1) return chapter;

              // Update the policy - using type assertion
              const updatedPolicies = [...chapter.policies];

              // Add isNew property using type assertion
              (updatedPolicies[policyIndex] as any).isNew = false;

              return {
                ...chapter,
                policies: updatedPolicies,
              };
            });

            updateManual(
              manual.id,
              updatedManual,
              () => {
                removeManualLock(
                  manual.id,
                  () => {
                    queryClient.invalidateQueries('draft_manuals');
                    queryClient.invalidateQueries(['draft_manual', manual.id]);
                    queryClient.invalidateQueries(['draft_policy', manual.id, id]);
                    setIsRejectingNew(false);
                    enqueueSnackbar(t('draftManuals.policy.newPolicy.rejected'), { variant: 'info' });
                    resolve();
                  },
                  (error: ServerErrorResponse) => {
                    errorHelper.enqueueErrors(error);
                    setIsRejectingNew(false);
                    reject(error);
                  }
                );
              },
              (error: ServerErrorResponse) => {
                removeManualLock(
                  manual.id,
                  () => { },
                  () => { }
                );
                errorHelper.enqueueErrors(error);
                setIsRejectingNew(false);
                reject(error);
              }
            );
          },
          (error: ServerErrorResponse) => {
            errorHelper.enqueueErrors(error);
            setIsRejectingNew(false);
            reject(error);
          }
        );
      });
    } catch (error) {
      console.error('Error rejecting new policy:', error);
    }
  }, [policyData, manual, id, getManualLock, updateManual, removeManualLock, queryClient, t]);

  const acceptDisabledPolicy = useCallback(async () => {
    if (!policyData) return;

    setIsAcceptingDisabled(true);
    try {
      await new Promise<void>((resolve, reject) => {
        getManualLock(
          manual.id,
          (lock) => {
            // Create a deep copy of the manual
            const updatedManual: DraftManual = JSON.parse(JSON.stringify(manual));

            // Find the chapter containing our policy and update it
            updatedManual.chapters = updatedManual.chapters.map((chapter) => {
              // Skip if chapter has no policies
              if (!chapter.policies) return chapter;

              // Check if this chapter contains our policy
              const policyIndex = chapter.policies.findIndex((policy) => policy.id === id);
              if (policyIndex === -1) return chapter;

              // Update the policy to DISABLED state
              const updatedPolicies = [...chapter.policies];
              const policy = updatedPolicies[policyIndex];
              const updatedPolicy = {
                ...policy,
                state: 'DISABLED' as 'DISABLED',
              };

              updatedPolicies[policyIndex] = updatedPolicy;

              return {
                ...chapter,
                policies: updatedPolicies,
              };
            });

            updateManual(
              manual.id,
              updatedManual,
              () => {
                // Process the issued change decision to accept the disabled policy
                const policyUpdate = pendingUpdates?.policies[id];
                if (policyUpdate?.policyDisabledChangesetId) {
                  processIssuedPolicyChangeDecision(
                    manual.id,
                    policyUpdate.policyDisabledChangesetId,
                    'ACCEPT',
                    () => {
                      removeManualLock(
                        manual.id,
                        () => {
                          queryClient.invalidateQueries('draft_manuals');
                          queryClient.invalidateQueries(['draft_manual', manual.id]);
                          queryClient.invalidateQueries(['draft_policy', manual.id, id]);
                          queryClient.invalidateQueries(['draft_manual_pending_updates', manual.id]);
                          setIsAcceptingDisabled(false);
                          enqueueSnackbar('Policy disabled successfully', { variant: 'success' });
                          resolve();
                        },
                        (error: ServerErrorResponse) => {
                          errorHelper.enqueueErrors(error);
                          setIsAcceptingDisabled(false);
                          reject(error);
                        }
                      );
                    },
                    (error: ServerErrorResponse) => {
                      removeManualLock(
                        manual.id,
                        () => { },
                        () => { }
                      );
                      errorHelper.enqueueErrors(error);
                      setIsAcceptingDisabled(false);
                      reject(error);
                    }
                  );
                } else {
                  removeManualLock(
                    manual.id,
                    () => {
                      queryClient.invalidateQueries('draft_manuals');
                      queryClient.invalidateQueries(['draft_manual', manual.id]);
                      queryClient.invalidateQueries(['draft_policy', manual.id, id]);
                      queryClient.invalidateQueries(['draft_manual_pending_updates', manual.id]);
                      setIsAcceptingDisabled(false);
                      enqueueSnackbar('Policy disabled successfully', { variant: 'success' });
                      resolve();
                    },
                    (error: ServerErrorResponse) => {
                      errorHelper.enqueueErrors(error);
                      setIsAcceptingDisabled(false);
                      reject(error);
                    }
                  );
                }
              },
              (error: ServerErrorResponse) => {
                removeManualLock(
                  manual.id,
                  () => { },
                  () => { }
                );
                errorHelper.enqueueErrors(error);
                setIsAcceptingDisabled(false);
                reject(error);
              }
            );
          },
          (error: ServerErrorResponse) => {
            errorHelper.enqueueErrors(error);
            setIsAcceptingDisabled(false);
            reject(error);
          }
        );
      });
    } catch (error) {
      console.error('Error accepting disabled policy:', error);
      setIsAcceptingDisabled(false);
    }
  }, [
    policyData,
    manual,
    id,
    getManualLock,
    updateManual,
    removeManualLock,
    processIssuedPolicyChangeDecision,
    queryClient,
  ]);

  const rejectDisabledPolicy = useCallback(async () => {
    if (!policyData) return;

    setIsRejectingDisabled(true);
    try {
      await new Promise<void>((resolve, reject) => {
        // Process the issued change decision to reject the disabled policy
        const policyUpdate = pendingUpdates?.policies[id];
        if (policyUpdate?.policyDisabledChangesetId) {
          processIssuedPolicyChangeDecision(
            manual.id,
            policyUpdate.policyDisabledChangesetId,
            'REJECT',
            () => {
              queryClient.invalidateQueries('draft_manuals');
              queryClient.invalidateQueries(['draft_manual', manual.id]);
              queryClient.invalidateQueries(['draft_policy', manual.id, id]);
              queryClient.invalidateQueries(['draft_manual_pending_updates', manual.id]);
              setIsRejectingDisabled(false);
              enqueueSnackbar('Policy disabled change rejected', { variant: 'info' });
              resolve();
            },
            (error: ServerErrorResponse) => {
              errorHelper.enqueueErrors(error);
              setIsRejectingDisabled(false);
              reject(error);
            }
          );
        } else {
          setIsRejectingDisabled(false);
          resolve();
        }
      });
    } catch (error) {
      console.error('Error rejecting disabled policy:', error);
      setIsRejectingDisabled(false);
    }
  }, [policyData, manual, id, processIssuedPolicyChangeDecision, queryClient, pendingUpdates]);

  const contextValue = useMemo(
    () => ({
      autoSave,
      committedPolicyQuery,
      currentSection,
      disableSave,
      enabledFilter,
      disabledFilter,
      filteredSections,
      filterSubsections,
      gettingLock,
      id,
      isOpenModifyPolicy,
      isSaving,
      handleAcquireLockClick,
      handleInsertSectionAfter,
      handleInsertSubSectionAfter,
      handleModifyClick,
      handleModifyPolicyDone,
      handleCancel,
      handleSave,
      handleSectionChange,
      handleSelectSection,
      handleUpdateDraftStatus,
      policyData,
      policyMode,
      policyNumber,
      policyQuery,
      revertChanges,
      setEnabledFilter,
      setDisabledFilter,
      setGettingLock,
      setIsOpenModifyPolicy,
      updatePolicyData,
      addAttachmentsToSection,
      acceptNewPolicy,
      rejectNewPolicy,
      acceptDisabledPolicy,
      rejectDisabledPolicy,
      isAcceptingNew,
      isRejectingNew,
      isAcceptingDisabled,
      isRejectingDisabled,
      addCrossReferenceToSection,
      removeCrossReferenceFromSection,
      accreditationTags: transformedAccreditationTags,
    }),
    [
      committedPolicyQuery.data,
      currentSection,
      enabledFilter,
      disabledFilter,
      filteredSections,
      filterSubsections,
      id,
      isOpenModifyPolicy,
      isSaving,
      policyData,
      policyMode,
      policyNumber,
      policyQuery.data,
      gettingLock,
      acceptNewPolicy,
      rejectNewPolicy,
      acceptDisabledPolicy,
      rejectDisabledPolicy,
      isAcceptingNew,
      isRejectingNew,
      isAcceptingDisabled,
      isRejectingDisabled,
      autoSave,
      handleAcquireLockClick,
      handleInsertSectionAfter,
      handleInsertSubSectionAfter,
      handleModifyClick,
      handleModifyPolicyDone,
      handleCancel,
      handleSave,
      handleSectionChange,
      handleSelectSection,
      handleUpdateDraftStatus,
      revertChanges,
      setEnabledFilter,
      setDisabledFilter,
      setGettingLock,
      setIsOpenModifyPolicy,
      updatePolicyData,
      addAttachmentsToSection,
      disableSave,
      transformedAccreditationTags,
    ]
  );

  return <PolicyEditorContext.Provider value={contextValue}>{children}</PolicyEditorContext.Provider>;
};

export const usePolicyEditorContext = () => {
  const context = useContext(PolicyEditorContext);
  if (!context) {
    throw new Error('usePolicyEditorContext must be used within a PolicyEditorProvider');
  }
  return context;
};
